import { useEffect, useState } from 'react';
import { GoArrowRight } from 'react-icons/go';
import { PRODUCT_URLS } from '@podus/utils';

import podusAiLogo from './assets/podus-ai.svg';
import podusAidLogo from './assets/podus-aids.svg';
import podusAppointmentLogo from './assets/podus-appointment.svg';
import podusContractLogo from './assets/podus-contract.svg';
import podusCheckerLogo from './assets/podus-cheaker.svg';
import podusMultidoorLogo from './assets/podus-multidoor.svg';

const previews = [
    {
        title: 'Podus AI',
        description: 'Your AI-powered legal assistant for instant guidance, document drafting, and legal clarity.',
    },
    {
        title: 'Contract Management',
        description: 'Create, review, and manage legal contracts with AI-powered analysis and expert review.',
    },
];

const products = [
    {
        logo: podusAiLogo,
        title: 'Podus AI',
        description: 'Ask legal questions, get instant AI-powered answers now.',
        href: PRODUCT_URLS.chatpodusai,
    },
    {
        logo: podusAidLogo,
        title: 'Podus Aid',
        description: 'Legal aid access made simple, fast, and fair.',
        href: PRODUCT_URLS.podusaid,
    },
    {
        logo: podusAppointmentLogo,
        title: 'Podus Appointment',
        description: 'Book appointments with verified legal professionals.',
        href: PRODUCT_URLS.podusappointment,
    },
    {
        logo: podusContractLogo,
        title: 'Podus Contract',
        description: 'Create, review, and manage legal contracts with ease.',
        href: PRODUCT_URLS.poduscontract,
    },
    {
        logo: podusCheckerLogo,
        title: 'Podus Checker',
        description: 'Background investigation and compliance checking platform.',
        href: PRODUCT_URLS.poduschecker,
    },
    {
        logo: podusMultidoorLogo,
        title: 'Podus Multidoor',
        description: 'File, track, and resolve disputes through digital court-connected resolution.',
        href: PRODUCT_URLS.podusmultidoor,
        comingSoon: true,
    },
];

function SkeletonCard({ title, sections, className = '' }: { title: string; sections: string[]; className?: string }) {
    return (
        <div
            className={`rounded-xl border border-gray-100 bg-white p-5 shadow-[0_2px_12px_rgba(0,0,0,0.06)] ${className}`}
        >
            <h4 className="text-secondary mb-3.5 text-xs font-bold">{title}</h4>
            {sections.map((section, i) => (
                <div key={i} className={i > 0 ? 'mt-3' : ''}>
                    <p className="text-secondary/60 mb-1.5 text-[9px] font-semibold">{section}</p>
                    <div className="space-y-1">
                        <div className="h-[3px] w-full rounded-full bg-gray-100" />
                        <div className="h-[3px] w-[85%] rounded-full bg-gray-100" />
                    </div>
                    <div className="mt-1.5 flex gap-1">
                        <div className="h-[3px] flex-1 rounded-sm bg-gray-100" />
                        <div className="h-[3px] flex-1 rounded-sm bg-gray-100" />
                        <div className="h-[3px] flex-1 rounded-sm bg-gray-100" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export interface ServicesMegaMenuProps {
    open: boolean;
    dropdownRef: React.RefObject<HTMLDivElement | null>;
}

export default function ServicesMegaMenu({ open, dropdownRef }: ServicesMegaMenuProps) {
    const [previewIndex, setPreviewIndex] = useState(0);

    useEffect(() => {
        if (!open) return;
        const interval = setInterval(() => {
            setPreviewIndex((prev) => (prev + 1) % previews.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [open]);

    const preview = previews[previewIndex];

    return (
        <div ref={dropdownRef} className="pointer-events-none fixed left-0 right-0 top-[76px] hidden lg:block">
            <div
                className={`pointer-events-auto ml-[5%] flex max-w-[90%] origin-top overflow-hidden border border-[#E0E0E0]  transition-all duration-300 ease-out ${
                    open
                        ? 'translate-y-0 scale-y-100 opacity-100'
                        : 'pointer-events-none -translate-y-2 scale-y-95 opacity-0'
                }`}
            >
                {/* Left — Products Grid */}
                <div className="flex-1 bg-white px-8 py-7 xl:px-10 xl:py-9">
                    <h3 className="text-secondary mb-6 text-lg font-bold">Products</h3>

                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-4 xl:gap-x-6 xl:gap-y-5">
                        {products.map((product, i) => (
                            <a
                                key={i}
                                href={product.href}
                                className="group px-5 py-4 transition-all duration-200 hover:bg-[#F5F5F5]"
                            >
                                <div className="mb-3 flex items-center gap-3">
                                    <img
                                        src={product.logo}
                                        alt={product.title}
                                        className="h-10 w-10 flex-shrink-0 rounded-xl object-contain grayscale transition-[filter] duration-200 group-hover:grayscale-0"
                                    />
                                    <span className="text-secondary flex-1 min-w-0 truncate text-sm font-bold leading-snug">
                                        {product.title}
                                        {'comingSoon' in product && product.comingSoon && (
                                            <span className="bg-primary/10 text-primary ml-2 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full align-middle">
                                                Coming Soon
                                            </span>
                                        )}
                                    </span>
                                    <GoArrowRight className="text-secondary/30 group-hover:text-secondary/60 h-4 w-4 flex-shrink-0 transition-colors" />
                                </div>
                                <p className="text-secondary-100 truncate text-xs leading-relaxed">
                                    {product.description}
                                </p>
                            </a>
                        ))}
                    </div>
                </div>

                {/* Right — Preview Panel */}
                <div className="flex w-[360px] flex-col bg-white px-8 py-7 xl:w-[400px] xl:px-10 xl:py-9">
                    <div className="relative min-h-[240px] flex-1">
                        <SkeletonCard
                            title="My Contract"
                            sections={['Summary', 'Experience', 'Skills']}
                            className="absolute left-0 top-4 z-[1] w-[155px] xl:w-[170px]"
                        />
                        <SkeletonCard
                            title="Podus Legal Assistant"
                            sections={['Summary', 'Experience', 'Skills']}
                            className="absolute left-[90px] top-0 z-[2] w-[155px] xl:left-[100px] xl:w-[170px]"
                        />
                        <SkeletonCard
                            title="Podus Checker"
                            sections={['Summary', 'Experience']}
                            className="absolute right-0 top-6 z-[3] w-[145px] xl:w-[160px]"
                        />
                    </div>

                    <h4
                        className="text-secondary mt-5 text-base font-bold transition-opacity duration-300"
                        key={preview?.title}
                    >
                        {preview?.title}
                    </h4>
                    <p
                        className="text-secondary-100 mt-1.5 max-w-xs text-xs leading-relaxed transition-opacity duration-300"
                        key={preview?.description}
                    >
                        {preview?.description}
                    </p>

                    <div className="mt-4 flex items-center gap-1.5">
                        {previews.map((_, i) => (
                            <span
                                key={i}
                                className={`rounded-full transition-all duration-300 ${
                                    previewIndex === i ? 'bg-secondary h-2.5 w-5' : 'bg-secondary/15 h-2 w-2'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
